import { useState, useEffect } from "react";
import api from "../api";
import Modal from "../components/Modal";

function monthOptions() {
  // Last 18 months back from now, plus a few forward, labeled "Jan 2026" etc.
  const opts = [];
  const now = new Date();
  for (let i = -6; i <= 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const label = d.toLocaleString("en", {month:"long", year:"numeric"});
    opts.push({value, label});
  }
  return opts;
}
function currentMonthValue() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`;
}

function downloadCSV(filename, rows, headers) {
  const esc = v => `"${String(v ?? "").replace(/"/g,'""')}"`;
  const lines = [headers.map(h=>esc(h.label)).join(",")];
  rows.forEach(r => lines.push(headers.map(h=>esc(typeof h.get==="function" ? h.get(r) : r[h.key])).join(",")));
  const blob = new Blob([lines.join("\n")], {type:"text/csv"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function useEmployeeList() {
  const [employees, setEmployees] = useState([]);
  useEffect(() => { api.get("/portal/admin/users").then(d => setEmployees(d.users || [])).catch(()=>{}); }, []);
  return employees;
}

function MonthPicker({value, onChange}) {
  const opts = monthOptions();
  return (
    <select className="inp" style={{width:"auto"}} value={value} onChange={e=>onChange(e.target.value)}>
      {opts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function EmployeeFilter({value, onChange}) {
  const employees = useEmployeeList();
  return (
    <select className="inp" style={{width:"auto"}} value={value} onChange={e=>onChange(e.target.value)}>
      <option value="">All Employees</option>
      {employees.map(e=><option key={e.employee_id} value={e.employee_id}>{e.name} — {e.employee_id}</option>)}
    </select>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("attendance");
  const [month, setMonth] = useState(currentMonthValue());
  const [emp, setEmp] = useState("");

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div>
          <h2 className="page-title">Reports</h2>
          <p className="page-sub">Monthly attendance, timesheet and leave reports</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <EmployeeFilter value={emp} onChange={setEmp}/>
          <MonthPicker value={month} onChange={setMonth}/>
        </div>
      </div>
      <div className="tabs" style={{marginTop:14}}>
        {[["attendance","Attendance"],["timesheet","Timesheet"],["leaves","Leave"]].map(([id,label]) => (
          <button key={id} className={`tab ${tab===id?"active":""}`} onClick={()=>setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === "attendance" && <AttendanceReport month={month} emp={emp}/>}
      {tab === "timesheet"  && <TimesheetReport month={month} emp={emp}/>}
      {tab === "leaves"     && <LeaveReport month={month} emp={emp}/>}
    </div>
  );
}

const CODE_COLORS = {
  P: "#16a34a", A: "#ef4444", H: "#0ea5e9", W: "#94a3b8",
};
function codeColor(code) {
  if (!code) return "#cbd5e1";
  if (CODE_COLORS[code]) return CODE_COLORS[code];
  if (code.includes("/")) return "#f59e0b"; // half-day combo
  return "#8b5cf6"; // leave type code
}

function AttendanceReport({month, emp}) {
  const [data, setData] = useState({day_labels:[], items:[]});
  const [showTimes, setShowTimes] = useState(false);

  useEffect(() => {
    const q = emp ? `?month=${month}&emp=${emp}` : `?month=${month}`;
    api.get(`/admin/reports/attendance${q}`).then(d => setData(d));
  }, [month, emp]);

  const exportCsv = () => {
    const headers = [
      {label:"Employee ID", key:"employee_id"}, {label:"Name", key:"employee_name"},
      ...data.day_labels.map((lbl,i) => ({label: lbl, get: r => r.cells[String(i+1)] || ""})),
    ];
    downloadCSV(`attendance_${month}.csv`, data.items, headers);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",margin:"12px 0"}}>
        <div style={{display:"flex",gap:16,fontSize:12,color:"var(--muted)",flexWrap:"wrap"}}>
          <span><b style={{color:CODE_COLORS.P}}>P</b> Present</span>
          <span><b style={{color:CODE_COLORS.A}}>A</b> Absent</span>
          <span><b style={{color:CODE_COLORS.H}}>H</b> Holiday</span>
          <span><b style={{color:CODE_COLORS.W}}>W</b> Weekend</span>
          <span><b style={{color:"#8b5cf6"}}>CL/EL/..</b> Leave type</span>
          <span><b style={{color:"#f59e0b"}}>0.5X/0.5Y</b> Half day</span>
        </div>
        <div style={{display:"flex",gap:8}}>
          <label style={{display:"flex",gap:6,alignItems:"center",fontSize:13,fontWeight:400}}>
            <input type="checkbox" checked={showTimes} onChange={e=>setShowTimes(e.target.checked)}/>
            Show check-in/out times
          </label>
          <button className="btn small" onClick={exportCsv}>Export CSV</button>
        </div>
      </div>
      <div className="card" style={{overflowX:"auto"}}>
        <table className="tbl" style={{fontSize:12}}>
          <thead>
            <tr>
              <th style={{position:"sticky",left:0,background:"var(--card-bg,#fff)"}}>Employee</th>
              {data.day_labels.map((lbl,i)=><th key={i} style={{whiteSpace:"nowrap",textAlign:"center"}}>{lbl}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.items.map(r=>(
              <tr key={r.employee_id}>
                <td style={{position:"sticky",left:0,background:"var(--card-bg,#fff)",whiteSpace:"nowrap"}}>
                  <b>{r.employee_name}</b><div style={{fontSize:10,color:"var(--muted)"}}>{r.employee_id}</div>
                </td>
                {data.day_labels.map((_,i)=>{
                  const key = String(i+1);
                  const code = r.cells[key] || "";
                  const time = r.times[key];
                  return (
                    <td key={i} style={{textAlign:"center",whiteSpace:"nowrap",color:codeColor(code),fontWeight:600}}>
                      {showTimes && time ? <span style={{fontWeight:400,color:"var(--muted)",fontSize:10}}>{time}</span> : code}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={data.day_labels.length+1} style={{textAlign:"center",color:"var(--muted)"}}>No employees found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TimesheetReport({month, emp}) {
  const [data, setData] = useState({day_labels:[], items:[]});
  const [drill, setDrill] = useState(null);
  useEffect(() => {
    const q = emp ? `?month=${month}&emp=${emp}` : `?month=${month}`;
    api.get(`/admin/reports/timesheet${q}`).then(d=>setData(d));
  }, [month, emp]);
  const exportCsv = () => {
    const headers = [
      {label:"Employee ID", key:"employee_id"}, {label:"Name", key:"employee_name"},
      ...data.day_labels.map((lbl,i) => ({label: lbl, get: r => r.cells[String(i+1)] || ""})),
      {label:"Total Hours", key:"total_hours"},
    ];
    downloadCSV(`timesheet_${month}.csv`, data.items, headers);
  };
  return (
    <div>
      <div style={{display:"flex",justifyContent:"flex-end",margin:"12px 0"}}>
        <button className="btn small" onClick={exportCsv}>Export CSV</button>
      </div>
      <div className="card" style={{overflowX:"auto"}}>
        <table className="tbl" style={{fontSize:12}}>
          <thead>
            <tr>
              <th style={{position:"sticky",left:0,background:"var(--card-bg,#fff)"}}>Employee</th>
              {data.day_labels.map((lbl,i)=><th key={i} style={{whiteSpace:"nowrap",textAlign:"center"}}>{lbl}</th>)}
              <th style={{whiteSpace:"nowrap"}}>Total</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(r=>(
              <tr key={r.employee_id}>
                <td style={{position:"sticky",left:0,background:"var(--card-bg,#fff)",whiteSpace:"nowrap"}}>
                  <b>{r.employee_name}</b><div style={{fontSize:10,color:"var(--muted)"}}>{r.employee_id}</div>
                </td>
                {data.day_labels.map((_,i)=>{
                  const day = i+1;
                  const v = r.cells[String(day)];
                  return (
                    <td key={i}
                      onClick={()=>v && setDrill({employeeId:r.employee_id, employeeName:r.employee_name, day, month})}
                      style={{textAlign:"center",color:v?"#0ea5e9":"#cbd5e1",fontWeight:v?600:400,
                              cursor:v?"pointer":"default",textDecoration:v?"underline":"none"}}>
                      {v || "-"}
                    </td>
                  );
                })}
                <td><b>{r.total_hours}</b></td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={data.day_labels.length+2} style={{textAlign:"center",color:"var(--muted)"}}>No employees found.</td></tr>}
          </tbody>
        </table>
      </div>
      {drill && <TimesheetDayModal drill={drill} onClose={()=>setDrill(null)}/>}
    </div>
  );
}

function TimesheetDayModal({drill, onClose}) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const dateStr = `${drill.month}-${String(drill.day).padStart(2,"0")}`;
    api.get(`/admin/reports/timesheet_day?emp=${drill.employeeId}&date=${dateStr}`).then(setData);
  }, [drill]);

  return (
    <Modal title={drill.employeeName + " -- " + drill.day + "/" + drill.month} onClose={onClose}>
      {!data ? <div style={{textAlign:"center",color:"var(--muted)"}}>Loading...</div> : (
        <table className="tbl">
          <thead><tr><th>Client</th><th>Project</th><th>Job</th><th>Description</th><th>Hours</th></tr></thead>
          <tbody>
            {data.items.map(e=>(
              <tr key={e.id}>
                <td>{e.client}</td><td>{e.project}</td><td>{e.job}</td>
                <td style={{maxWidth:220,whiteSpace:"normal"}}>{e.description}</td>
                <td><b>{e.hours}</b></td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={5} style={{textAlign:"center",color:"var(--muted)"}}>No entries.</td></tr>}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

function LeaveReport({month, emp}) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const q = emp ? `?month=${month}&emp=${emp}` : `?month=${month}`;
    api.get(`/admin/reports/leaves${q}`).then(d=>setItems(d.items||[]));
  }, [month, emp]);

  const headers = [
    {label:"Employee ID", key:"employee_id"}, {label:"Name", key:"employee_name"},
    {label:"Leave Type", key:"leave_type"}, {label:"From", key:"from_date"},
    {label:"To", key:"to_date"}, {label:"Days", key:"days"},
    {label:"Half Day", get: r => r.is_half_day ? "Yes" : "No"},
    {label:"Reason", key:"reason"}, {label:"Approved By", key:"approved_by"},
  ];

  const totalDays = items.reduce((sum, r) => sum + (r.days || 0), 0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",margin:"12px 0"}}>
        <div style={{fontSize:13,color:"var(--muted)"}}>
          {items.length} leave(s), {Math.round(totalDays*100)/100} total day(s) this month
        </div>
        <button className="btn small" onClick={()=>downloadCSV(`leave_report_${month}.csv`, items, headers)}>Export CSV</button>
      </div>
      <div className="card">
        <table className="tbl">
          <thead><tr>
            <th>Employee</th><th>Leave Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Approved By</th>
          </tr></thead>
          <tbody>
            {items.map((r,i)=>(
              <tr key={i}>
                <td><b>{r.employee_name}</b><div style={{fontSize:11,color:"var(--muted)"}}>{r.employee_id}</div></td>
                <td><span className="pill blue" style={{fontSize:10}}>{r.leave_code}</span> {r.leave_type}</td>
                <td>{r.from_date}</td>
                <td>{r.to_date}</td>
                <td>{r.days}{r.is_half_day && " (half)"}</td>
                <td style={{maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.reason||"-"}</td>
                <td style={{fontSize:12,color:"var(--muted)"}}>{r.approved_by}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={7} style={{textAlign:"center",color:"var(--muted)"}}>No approved leaves this month.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
